import React from 'react';
import { FileText, BookOpen, ExternalLink } from 'lucide-react';
import { useFontSizes } from '../../contexts/FontSizeContext';
import { useI18n } from '../../i18n/I18nContext';
import { safeUrl, openInWorkingTab } from '../../utils/navigate';
import type { WidgetProps } from './types';

export const ArticlesWidget: React.FC<WidgetProps> = ({ config, results }) => {
  const { heading, body } = useFontSizes();
  const { t } = useI18n();
  const articles = results?.articles ?? [];
  const items = config.maxItems ? articles.slice(0, config.maxItems) : articles.slice(0, 5);

  if (items.length === 0) return null;

  return (
    <section className="mb-8">
      <h2 className={`flex items-center gap-2 ${heading} font-semibold uppercase tracking-wider text-[#020202] mb-4`}>
        <FileText className="h-4 w-4" />
        {config.title ?? t('section.articles')}
        <span className="text-gray-400 font-normal">({items.length})</span>
      </h2>
      <div className="flex flex-col gap-3">
        {items.map((article) => (
          <a
            key={article.id}
            href={safeUrl(article.url)}
            onClick={(e) => openInWorkingTab(article.url, e)}
            className="botilia-card group flex items-start gap-3 p-3"
          >
            {article.imageUrl ? (
              <img
                src={article.imageUrl}
                alt=""
                className="w-14 h-14 object-cover flex-shrink-0"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = 'none';
                }}
              />
            ) : (
              <div className="w-14 h-14 bg-gray-100 flex items-center justify-center flex-shrink-0">
                <BookOpen className="h-6 w-6 text-gray-300" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              {article.category && (
                <span className="text-[10px] font-semibold uppercase tracking-wider text-brand-ink mb-1 block">
                  {article.category}
                </span>
              )}
              <h3 className={`${body} font-medium text-gray-800 line-clamp-2 group-hover:text-brand transition-colors`}>
                {article.title}
              </h3>
            </div>
            <ExternalLink className="h-4 w-4 text-gray-300 flex-shrink-0 mt-0.5 group-hover:text-brand transition-colors" />
          </a>
        ))}
      </div>
    </section>
  );
};
